import canvas from '@classes/canvas'
import { getConfigBoolean, getThemeName } from '@config'
import type { MeterOptions } from '@constants/gauges_meters_consts'
import { MeterId, MeterType } from '@constants/gauges_meters_consts'
import type { AppState } from '@constants/types'
import usePrevious from '@constants/usePrevious'
import { getPropertyValue } from '@constants/utils'
import type { NullableTimer } from '@shared_types/global_types'
import type { Metric } from '@shared_types/vessel_data'
import type { FeatureCollection } from 'geojson'
import { debounce, isNumber } from 'lodash'
import type { CSSProperties } from 'react'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { drawCompassGauge, drawDepthGauge, drawEarthGauge, drawEcoScoreGauge, drawHeadingGauge, drawMeccaGauge, drawPressureGauge, drawSpeedGauge, drawTemperatureGauge, drawTimeGauge, drawWindGauge } from './gauges'
import type { GaugeDrawResult } from './gauges/gauge_types'

export interface MainParams {
  center: number
  radius: number
  value: object | number
  maxValue?: number
  minValue?: number
  prevValue: number
  currentValue: number
  ctx: CanvasRenderingContext2D
  dpx: number
  unit?: string
  redraw: (unitDx?: number) => void
  formatter: (v: number) => string
}

export interface StyleParams {
  color?: string
  arcColor: string
  size?: number
  strokeWidth?: number
}

interface MeterProps<T extends MeterType = MeterType> {
  id: string
  type: T
  value: number | object
  options?: T extends keyof MeterOptions ? MeterOptions[T] : never
  formatter?: (v: number) => string
  label?: string
  unit?: string
  minValue?: number
  maxValue?: number
  backgroundColor?: string
  style?: CSSProperties
  layout?: 'grid'
  index?: number
  title?: string
  windDirection?: number
  windSpeed?: number
  windSpeedUnit?: string
  seaTemp?: number
  color?: string
  strokeWidth?: number
  size?: number
  sunrise?: Date | null
  sunset?: Date | null
  meccaLocation?: number
  meccaDistance?: number
  isBig?: boolean
  lat?: number
  long?: number
  time?: string
  data?: Metric[]
  worldMap?: FeatureCollection
}

const DEBOUNCE_TIMEOUT = 100

export default function Meter<T extends MeterType> (props: MeterProps<T>) {
  const { id, value, type, label, unit, minValue, maxValue, backgroundColor } = props

  const windDirection = useSelector((appState: AppState) => appState.main.windDirection.value)
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | undefined>(undefined)
  const [size, setSize] = useState(0)
  const [currentValue, setCurrentValue] = useState(0)
  const [animateId, setAnimateId] = useState(-1)
  const [animCtx, setAnimCtx] = useState<NullableTimer>(null)
  const [isMeccaVisible, setIsMeccaVisible] = useState(false)
  const prevValue = usePrevious(value)
  const yisTheme = getThemeName()

  const calculatePrevValue = (prev?: number | object, minValue?: number) => {
    if (prev) {
      return Number(prev)
    } else if (minValue) {
      return minValue
    } else {
      return 0
    }
  }

  const draw = (ctx: CanvasRenderingContext2D, prev?: number | object) => {
    const {
      layout,
      strokeWidth,
      sunrise,
      sunset,
      isBig,
      meccaDistance,
      meccaLocation,
      windSpeedUnit,
      windSpeed,
      seaTemp,
      data,
      lat,
      long,
      worldMap
    } = props

    let { value } = props

    const dpx = size / 100
    const center = size / 2 - dpx * 0.5
    const radius = center - dpx
    const fontSize = dpx * 20

    const prevValue = calculatePrevValue(prev, minValue)

    if (value === 0 && minValue) {
      value = Number(minValue)
    }

    let formatter: (v: number) => string

    if (props.formatter) {
      formatter = props.formatter
    } else {
      formatter = (v: number) => v.toFixed(0)
    }

    // const redraw = (unitDx: number = 1.25) => {
    const redraw = () => {
      backgroundColor && canvas.drawPieSlice(ctx, center, center, radius, 1, Math.PI * 2, backgroundColor)
      label && canvas.drawTitle(ctx, center, label, dpx * 9)
    }

    const mainParams: MainParams = {
      center,
      radius,
      value,
      maxValue,
      minValue,
      prevValue,
      currentValue,
      ctx,
      dpx,
      unit,
      redraw,
      formatter
    }

    const arcColor = getPropertyValue('--meter-color')
    const color = props.color || getPropertyValue('--meter-font-color')
    const styleParams: StyleParams = { color, arcColor, size, strokeWidth }

    let res: GaugeDrawResult | null = null

    const options = props.options

    if (type === MeterType.TIME) {
      if (yisTheme === 'steam' && id === MeterId.LOCALE_CLOCK_WIDGET) {
        styleParams.arcColor = '#000000'
      }
      drawTimeGauge(id, sunrise, sunset, mainParams, styleParams, { isBig, ...options })
    } else if (type === MeterType.SPEED) {
      res = drawSpeedGauge(mainParams, styleParams)
    } else if (type === MeterType.TEMPERATURE) {
      res = drawTemperatureGauge(mainParams, styleParams, size, seaTemp, layout)
    } else if (type === MeterType.ECO_SCORE) {
      res = drawEcoScoreGauge(mainParams, styleParams)
    } else if (type === MeterType.WIND) {
      res = drawWindGauge(windSpeed, windSpeedUnit, mainParams, styleParams, windDirection)
    } else if (type === MeterType.HEADING) {
      res = drawHeadingGauge(mainParams, styleParams, isMeccaVisible, meccaLocation, windDirection, fontSize, layout)
    } else if (type === MeterType.COMPASS) {
      res = drawCompassGauge(mainParams, styleParams)
    } else if (type === MeterType.DEPTH) {
      if (isNumber(mainParams.value) && isNumber(mainParams.maxValue) && mainParams.value > mainParams.maxValue) {
        mainParams.value = mainParams.maxValue
      }
      res = drawDepthGauge(mainParams, styleParams, data, size)
    } else if (type === MeterType.PRESSURE) {
      res = drawPressureGauge(mainParams, styleParams)
    } else if (type === MeterType.MECCA) {
      res = drawMeccaGauge(meccaDistance, meccaLocation, mainParams, styleParams)
    } else if (type === MeterType.EARTH) {
      res = drawEarthGauge({ lat: lat as number, lng: long as number }, mainParams, styleParams, worldMap)
    }

    if (res) {
      setAnimateId(res.animateId)
      setCurrentValue(res.newCurrentValue)
    }
  }

  const deleteLastIdAnimation = (animationId: number) => {
    if (animationId >= 0) {
      cancelAnimationFrame(animationId)
    }
  }

  const drawCanvas = (myCanvas: HTMLCanvasElement) => {
    const result = canvas.setupCanvas(myCanvas)
    if (result.ctx) {
      setCtx(result.ctx)
      setSize(result.size)
    }
  }

  useEffect(() => {
    if (ctx) {
      const aContext = setTimeout(() => draw(ctx), DEBOUNCE_TIMEOUT)
      setAnimCtx(aContext)
    }
  }, [ctx])

  useEffect(() => {
    const myCanvas = document.getElementById(`meter_${id}_canvas`) as HTMLCanvasElement | null

    const onWindowResize = () =>
      debounce(() => {
        if (myCanvas) {
          drawCanvas(myCanvas)
        }
      }, DEBOUNCE_TIMEOUT)

    if (myCanvas) {
      drawCanvas(myCanvas)
      window.addEventListener('resize', onWindowResize)
    }

    setIsMeccaVisible(getConfigBoolean('model.mecca.visibility'))

    return () => {
      window.removeEventListener('resize', onWindowResize)

      if (animCtx) {
        clearTimeout(animCtx)
      }

      if (animateId) {
        cancelAnimationFrame(animateId)
        setAnimateId(-1)
      }
    }
  }, [])

  useEffect(() => {
    if (ctx) {
      deleteLastIdAnimation(animateId)
      ctx.clearRect(0, 0, size, size)
      draw(ctx, prevValue)
    }
  }, [props.value])

  useEffect(() => {
    if (ctx && props.worldMap) {
      ctx.clearRect(0, 0, size, size)
      draw(ctx, prevValue)
    }
  }, [ctx, props.worldMap])

  useEffect(() => {
    if (ctx) {
      draw(ctx, prevValue)
    }
  }, [ctx, props.seaTemp])

  return <div className="display-contents">
    <span className="preloadFont" />
    <canvas className="display-contents--cursor" id={`meter_${id}_canvas`} style={props.style} />
  </div>
}
